import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api";
import { toast } from "sonner";

const CartCtx = createContext(null);

const STORAGE_KEY = "mlobox_cart_v2";
const ZONE_KEY = "mlobox_zone_v1";

export const lineKey = (itemId, side, extras) =>
  [itemId, side || "", (extras || []).map((e) => e.id).sort().join("+")].join("|");

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
    } catch {
      return [];
    }
  });
  const [zone, setZone] = useState(() => localStorage.getItem(ZONE_KEY) || "Kilimani");

  useEffect(() => { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem(ZONE_KEY, zone); }, [zone]);

  const add = (item, { side = null, extras = [], quantity = 1 } = {}) => {
    const key = lineKey(item.id, side, extras);
    const price = item.price + extras.reduce((a, e) => a + e.price, 0);
    setItems((prev) => {
      if (prev.some((i) => i.key === key)) {
        return prev.map((i) => i.key === key ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, { key, item_id: item.id, name: item.name, image_url: item.image_url, side, extras, price, quantity }];
    });
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));
  const inc = (key) => setItems((prev) => prev.map((i) => i.key === key ? { ...i, quantity: i.quantity + 1 } : i));
  const dec = (key) => setItems((prev) => prev.flatMap((i) => {
    if (i.key !== key) return [i];
    if (i.quantity <= 1) return [];
    return [{ ...i, quantity: i.quantity - 1 }];
  }));
  const clear = () => setItems([]);
  const qtyFor = (itemId) => items.filter((i) => i.item_id === itemId).reduce((a, i) => a + i.quantity, 0);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  const refreshPrices = useCallback(async () => {
    const [m, e] = await Promise.all([api.get("/menu"), api.get("/extras")]);
    const menu = Object.fromEntries(m.data.map((i) => [i.id, i]));
    const ex = Object.fromEntries(e.data.map((x) => [x.id, x]));
    const prev = itemsRef.current;
    const merged = new Map();
    prev.forEach((l) => {
      const d = menu[l.item_id];
      if (!d || d.sold_out) return;
      const sides = d.sides || [];
      const side = sides.length ? (sides.includes(l.side) ? l.side : sides[0]) : null;
      const extras = (l.extras || []).filter((x) => ex[x.id] && (d.extra_ids || []).includes(x.id))
        .map((x) => ({ id: x.id, name: ex[x.id].name, price: ex[x.id].price }));
      const key = lineKey(d.id, side, extras);
      const price = d.price + extras.reduce((a, x) => a + x.price, 0);
      const qty = (merged.get(key)?.quantity || 0) + l.quantity;
      merged.set(key, { ...l, key, name: d.name, side, extras, price, quantity: qty });
    });
    const next = [...merged.values()];
    const removed = prev.filter((l) => !menu[l.item_id] || menu[l.item_id].sold_out).length;
    const optsChanged = next.length !== prev.length - removed || next.some((l, i) => l.key !== prev[i]?.key);
    const changed = optsChanged || removed > 0 || next.some((l, i) => l.price !== prev[i]?.price || l.name !== prev[i]?.name);
    if (changed) setItems(next);
    if (removed) toast.info("Some items in your cart are no longer available and were removed");
    else if (optsChanged) toast.info("Some options in your cart were updated");
  }, []);

  const totals = useMemo(() => {
    const count = items.reduce((a, i) => a + i.quantity, 0);
    const subtotal = items.reduce((a, i) => a + i.price * i.quantity, 0);
    return { count, subtotal };
  }, [items]);

  return (
    <CartCtx.Provider value={{ items, zone, setZone, add, remove, inc, dec, clear, qtyFor, refreshPrices, ...totals }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);

export const lineDetails = (i) => [i.side, ...(i.extras || []).map((e) => `+ ${e.name}`)].filter(Boolean).join(" · ");
